import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { WhatsAppCTA } from "@/components/WhatsApp";
import SocialIcons from "@/components/SocialIcons";
import { DEFAULT_WA_MESSAGES } from "@/config/site";
import { Award, GraduationCap, Dumbbell, HeartPulse, ArrowRight, Clock, ShieldCheck, BadgeCheck } from "lucide-react";
import { usePageCms } from "@/lib/siteSettings";

const FALLBACK_ACCENTS = ["#f47a20", "#1E9BE5", "#8ACC5C", "#B54BE6", "#F5B700", "#22C55E"];

// Explicit accent mapping — persistent per clinician so their brand colour
// doesn't drift when admins reorder the CMS or add new team members.
// Andrea → Red, Luca → Blue, Francesco → Green, Saimon → Purple.
const NAME_ACCENTS = {
  "Andrea Tuccillo":    "#f47a20",
  "Luca Tuccillo":      "#1E9BE5",
  "Francesco Pezzella": "#8ACC5C",
  "Saimon Silva":       "#B54BE6",
};

function accentFor(name, idx) {
  return NAME_ACCENTS[name] || FALLBACK_ACCENTS[idx % FALLBACK_ACCENTS.length];
}

function resolveMediaUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/api/")) return `${API}${url.slice(4)}`;
  return url;
}

const VALUES = [
  { icon: HeartPulse, title: "Human first", body: "Every plan starts with your goals and lifestyle — not a protocol from a textbook." },
  { icon: Dumbbell, title: "Load, don't rest", body: "Progressive, measured loading is the fastest, safest way out of pain." },
  { icon: GraduationCap, title: "Evidence-led", body: "We treat with what the research supports — and stay curious about what's next." },
  { icon: Award, title: "One team", body: "Sport therapy + S&C in one room means no hand-offs, no lost weeks between phases." },
];

export default function Team() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const cms = usePageCms("team");
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);

  // Booking destination for the page-level CTA (no clinician preselected).
  // Per-clinician "Book Andrea" buttons keep their own therapist redirect.
  const bookHref = user
    ? "/app/appointments"
    : `/register?redirect=${encodeURIComponent("/app/appointments")}`;

  useEffect(() => {
    axios.get(`${API}/public/cms/team`)
      .then((r) => setTeam(r.data || []))
      .catch(() => setTeam([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen fl-noise fl-page" style={{ background: "var(--fl-bg)", color: "var(--fl-text)" }}>
      <PublicHeader />

      {/* Hero */}
      {cms.isVisible("hero") && (
      <section className="pt-32 md:pt-40 pb-20 md:pb-24 px-6 bg-[#0A0A0A] fl-noise relative overflow-hidden">
        <div className="max-w-5xl mx-auto text-center relative">
          <div className="fl-label text-[color:var(--fl-orange)] mb-4">{cms.text("hero", "eyebrow", t("teamPage.eyebrow"))}</div>
          <h1 className="fl-heading text-5xl md:text-6xl leading-none">
            {cms.get("hero").heading ? cms.get("hero").heading : (<>{t("teamPage.h1a")} <span className="text-[color:var(--fl-orange)]">{t("teamPage.h1b")}</span>.<br />{t("teamPage.h1c")}</>)}
          </h1>
          <p className="mt-6 text-lg max-w-2xl mx-auto" style={{ color: "var(--fl-muted)" }}>
            {cms.text("hero", "body", t("teamPage.subtitle"))}
          </p>
        </div>
      </section>
      )}

      {/* Team grid */}
      <section className="py-24 md:py-32 px-6 bg-[#121212] border-t border-white/5">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-6 fl-stagger">
          {loading ? (
            [1, 2, 3, 4].map((i) => (
              <div key={i} className="fl-card p-8 animate-pulse">
                <div className="flex justify-center mb-6">
                  <div className="w-40 h-40 rounded-full bg-white/5" />
                </div>
                <div className="h-4 bg-white/5 rounded w-24 mx-auto" />
                <div className="h-6 bg-white/5 rounded w-40 mx-auto mt-3" />
                <div className="h-16 bg-white/5 rounded mt-5" />
              </div>
            ))
          ) : team.length === 0 ? (
            <div className="md:col-span-2 fl-card p-10 text-center text-sm" style={{ color: "var(--fl-muted)" }}>
              Team members coming soon.
            </div>
          ) : (
            team.map((m, idx) => {
              const accent = accentFor(m.name, idx);
              const isComingSoon = m.status === "coming_soon";
              return (
                <article
                  key={m.id}
                  className="fl-card p-8 relative overflow-hidden"
                  data-testid={`team-${m.id}`}
                >
                  <div
                    className="absolute -top-20 -right-20 w-56 h-56 rounded-full blur-3xl pointer-events-none"
                    style={{ background: `radial-gradient(circle, ${accent}22, transparent 70%)` }}
                  />

                  <div className="flex justify-center mb-6 relative">
                    <div
                      className="rounded-full overflow-hidden bg-white/5"
                      style={{
                        width: 172,
                        height: 172,
                        border: `4px solid ${accent}`,
                        boxShadow: `0 0 0 4px color-mix(in oklab, ${accent} 14%, transparent), 0 18px 40px -20px ${accent}66`,
                      }}
                    >
                      {m.photo_url ? (
                        <img src={resolveMediaUrl(m.photo_url)} alt={m.name} loading="lazy" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full grid place-items-center text-3xl fl-heading" style={{ color: accent, background: `color-mix(in oklab, ${accent} 10%, transparent)` }}>
                          {m.name?.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-center gap-2 mb-2">
                    <div className="fl-label" style={{ color: accent }}>{m.role || "Sport Rehabilitator"}</div>
                    {isComingSoon && (
                      <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider" style={{ color: "var(--fl-orange)", background: "color-mix(in oklab, var(--fl-orange) 14%, transparent)", border: "1px solid color-mix(in oklab, var(--fl-orange) 30%, transparent)" }}>
                        <Clock size={10} /> Joining soon
                      </span>
                    )}
                  </div>
                  <h3 className="fl-heading text-3xl mt-1 text-center">{m.name}</h3>
                  {m.bio && <p className="mt-4 text-sm leading-relaxed text-center" style={{ color: "var(--fl-muted)" }}>{m.bio}</p>}

                  {m.credentials && (
                    <div className="mt-5 pt-4 border-t border-white/5" data-testid={`team-${m.id}-credentials`}>
                      <div className="flex flex-wrap items-center justify-center gap-2">
                        {(m.credentials.registrations || []).map((r) => (
                          <span
                            key={r}
                            className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border"
                            style={{ borderColor: `color-mix(in oklab, ${accent} 40%, transparent)`, background: `color-mix(in oklab, ${accent} 10%, transparent)`, color: accent }}
                          >
                            <ShieldCheck size={11} /> {r}
                          </span>
                        ))}
                      </div>
                      {m.credentials.hcpc_number && (
                        <div className="mt-2 text-center text-[11px]" style={{ color: "var(--fl-muted)" }} data-testid={`team-${m.id}-hcpc`}>
                          HCPC registration: <span className="font-mono" style={{ color: "var(--fl-text)" }}>{m.credentials.hcpc_number}</span>
                          {m.credentials.hcpc_verified && (
                            <span className="ml-1.5 inline-flex items-center gap-0.5 text-emerald-400">· <BadgeCheck size={11} /> Verified on the HCPC Register</span>
                          )}
                        </div>
                      )}
                      {(m.credentials.qualifications || []).length > 0 && (
                        <ul className="mt-3 flex flex-col items-center gap-1 text-[11px]" style={{ color: "var(--fl-muted)" }}>
                          {m.credentials.qualifications.map((q) => (
                            <li key={q} className="flex items-start gap-1.5"><GraduationCap size={11} className="mt-0.5 shrink-0" style={{ color: accent }} /> {q}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}

                  {!isComingSoon && (
                    <div className="mt-6 flex flex-wrap gap-3 justify-center">
                      <Link
                        to={`/register?therapist=${encodeURIComponent(m.id)}&redirect=${encodeURIComponent("/app/appointments?clinician=" + m.id)}`}
                        className="fl-btn-ghost text-xs px-3 py-2 rounded-md"
                        data-testid={`team-${m.id}-book`}
                      >
                        {t("pub.team.bookWith", { name: (m.name || "").split(" ")[0] })}
                      </Link>
                      <WhatsAppCTA
                        message={`Hi Functional LAB, I would like to book an appointment with ${m.name}.`}
                        testid={`team-${m.id}-wa`}
                        variant="soft"
                        size="sm"
                      >
                        {t("pub.team.askOnWa")}
                      </WhatsAppCTA>
                    </div>
                  )}
                </article>
              );
            })
          )}
        </div>
      </section>

      {/* Values */}
      {cms.isVisible("values") && (
      <section className="py-24 md:py-32 px-6 bg-[#0A0A0A] border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="fl-label text-[color:var(--fl-orange)] mb-3">{cms.text("values", "eyebrow", t("teamPage.valuesEyebrow"))}</div>
            <h2 className="fl-heading text-4xl md:text-5xl">{cms.text("values", "heading", t("teamPage.valuesTitle"))}</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-4 fl-stagger">
            {VALUES.map((v) => {
              const Icon = v.icon;
              return (
                <div key={v.title} className="fl-card p-6">
                  <Icon size={22} className="text-[color:var(--fl-orange)]" />
                  <h4 className="fl-heading text-lg mt-4">{v.title}</h4>
                  <p className="mt-2 text-sm" style={{ color: "var(--fl-muted)" }}>{v.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      )}

      {/* CTA */}
      {cms.isVisible("cta") && (
      <section className="py-24 md:py-32 px-6 bg-gradient-to-b from-[#1A1A1A] via-[#170f0d] to-[#0A0A0A] border-t border-white/5">
        <div className="max-w-4xl mx-auto fl-card p-10 text-center relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 50% 0%, rgba(255,69,0,0.10), transparent 60%)" }} />
          <div className="relative">
            <h3 className="fl-heading text-3xl md:text-4xl">{cms.text("cta", "heading", t("teamPage.ctaTitle"))}</h3>
            <p className="mt-3 max-w-xl mx-auto text-sm" style={{ color: "var(--fl-muted)" }}>
              {cms.text("cta", "body", t("teamPage.ctaBody"))}
            </p>
            <Link to={bookHref} className="fl-btn text-base mt-6" data-testid="team-cta-book">
              {cms.text("cta", "ctaLabel", t("teamPage.ctaBook"))} <ArrowRight size={16} />
            </Link>
            <div className="mt-4 flex flex-wrap gap-3 justify-center items-center">
              <WhatsAppCTA message={DEFAULT_WA_MESSAGES.team} testid="team-cta-wa" variant="soft">
                {t("pub.wa.needHelp")}
              </WhatsAppCTA>
              <SocialIcons size={20} testid="team-socials" />
            </div>
          </div>
        </div>
      </section>
      )}

      <PublicFooter />
    </div>
  );
}
